import StatusPill from '@/components/console/status-pill';
import EmptyState from '@/components/empty-state';
import PageHeader from '@/components/page-header';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import ValidationErrors from '@/components/validation-errors';
import {
  removalsFrom,
  resolveKeys,
  toEditorLists,
  toPayload,
  type EditorField,
  type EditorList,
  type ReservedKeys,
} from '@/pages/apps/designs/components/design-state';
import ListEditor from '@/pages/apps/designs/components/list-editor';
import { app_design_path } from '@/rails/routes';
import type { AppList, AppShell, FieldType } from '@/types/platform';
import { router } from '@inertiajs/react';
import { Plus } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';

interface Props {
  app: AppShell;
  design: { lists: AppList[] };
  fieldTypes: FieldType[];
  reserved: ReservedKeys;
  schemaCurrent: boolean;
  schemaVersion: number | null;
  problems: string[];
}

const Show = ({
  app,
  design,
  fieldTypes,
  reserved,
  schemaCurrent,
  schemaVersion,
  problems,
}: Props) => {
  // What is saved now. Removals are measured against it, and it moves only when a save goes through.
  const [saved, setSaved] = useState(design.lists);
  const [lists, setLists] = useState(() => toEditorLists(design.lists));
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const nextId = useRef(0);

  const resolved = resolveKeys(lists, reserved);
  const removals = removalsFrom(saved, resolved);
  const removesSomething = removals.lists.length > 0 || removals.fields.length > 0;

  const newId = () => {
    nextId.current += 1;

    return `new-${nextId.current}`;
  };

  const newField = (label: string, required: boolean): EditorField => ({
    id: newId(),
    savedKey: null,
    label,
    type: 'text',
    required,
    optionsText: '',
  });

  const addList = () => {
    const list: EditorList = {
      id: newId(),
      savedKey: null,
      name: '',
      singular: '',
      titleFieldId: null,
      fields: [newField('Name', true)],
    };

    setLists((current) => [...current, list]);
  };

  const changeList = (listId: string, change: (list: EditorList) => EditorList) => {
    setLists((current) => current.map((list) => (list.id === listId ? change(list) : list)));
  };

  const save = () => {
    router.patch(
      app_design_path(app.id),
      { design: toPayload(resolved) },
      {
        preserveScroll: true,
        onStart: () => setSaving(true),
        onFinish: () => setSaving(false),
        onSuccess: (page) => {
          // The same page comes back either way. With no problems the design was saved,
          // so the editor starts again from what the server now holds.
          const next = page.props as Partial<Props>;

          if (next.design && (next.problems ?? []).length === 0) {
            setSaved(next.design.lists);
            setLists(toEditorLists(next.design.lists));
          }
        },
      }
    );
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (removesSomething) {
      setConfirming(true);
    } else {
      save();
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Design"
        description="The lists your app keeps, and the fields on each one."
        badges={
          schemaCurrent ? (
            <StatusPill tone="success">Saved and protected</StatusPill>
          ) : (
            <StatusPill tone="warning">Changes are not applied yet</StatusPill>
          )
        }
      />

      <p className="text-muted-foreground text-sm">
        {schemaCurrent
          ? 'This design is saved and its protection is applied.'
          : app.provisioned
            ? 'Save the design to apply its protection.'
            : 'Protection is applied when the app finishes setting up.'}
        {schemaVersion === null
          ? null
          : ` Protection was last applied as version ${schemaVersion}.`}
      </p>

      <ValidationErrors errors={problems} />

      <form onSubmit={submit} className="space-y-4">
        {resolved.length === 0 ? (
          <Card>
            <CardContent>
              <EmptyState
                title="No lists yet."
                description="A list holds one kind of record, such as jobs, equipment, or inspections."
              />
            </CardContent>
          </Card>
        ) : (
          resolved.map((list) => (
            <ListEditor
              key={list.id}
              list={list}
              fieldTypes={fieldTypes}
              onChange={(patch) => changeList(list.id, (current) => ({ ...current, ...patch }))}
              onRemove={() => setLists((current) => current.filter((item) => item.id !== list.id))}
              onAddField={() => {
                const field = newField('', false);

                changeList(list.id, (current) => ({
                  ...current,
                  fields: [...current.fields, field],
                }));
              }}
              onFieldChange={(fieldId, patch) =>
                changeList(list.id, (current) => ({
                  ...current,
                  fields: current.fields.map((field) =>
                    field.id === fieldId ? { ...field, ...patch } : field
                  ),
                }))
              }
              onFieldRemove={(fieldId) =>
                changeList(list.id, (current) => ({
                  ...current,
                  fields: current.fields.filter((field) => field.id !== fieldId),
                }))
              }
            />
          ))
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={addList}>
            <Plus aria-hidden="true" />
            Add a list
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save and remove these?</AlertDialogTitle>
            <AlertDialogDescription>
              Removed lists and fields keep their records, but they stop showing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="list-inside list-disc space-y-1 text-sm">
            {removals.lists.map((name) => (
              <li key={`list-${name}`}>The list {name}</li>
            ))}
            {removals.fields.map((entry) => (
              <li key={`fields-${entry.list}`}>
                From {entry.list}: {entry.fields.join(', ')}
              </li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={save}>Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Show;
